import { useState } from 'react'
import { isEqual } from 'lodash'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'

import TabTugasTambahan from '../../Perencanaan/TabTugasTambahan'
import PanelSummary from 'app/KinerjaPage/components/PanelSummary'
import { translateSKPPeriod } from 'app/KinerjaPage/utils/kinerja'
import { apiPaths, useAPIUpdateTugasTambahan } from 'api/kinerja/useAPIKinerja'
import DialogConfirmSaveEdit from '../components/DialogConfirmSaveEdit'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { useGetKinerjaQuerykeys } from 'api/kinerja/utils/api'

import type { Kinerja, KinerjaSelection } from 'types/kinerja'

interface EditTugasTambahanProps {
  skp: Kinerja
  isSKPFetched: boolean
}

export default function EditTugasTambahan({
  skp,
  isSKPFetched,
}: EditTugasTambahanProps) {
  const { mutateAsync } = useAPIUpdateTugasTambahan()
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()

  const handleClickBack = () => {
    router.back()
  }

  const [values, setValues] = useState<KinerjaSelection[]>(
    skp.reviewTugasTambahan
  )
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const queryKeys = useGetKinerjaQuerykeys(apiPaths.kinerja())

  const handleSave = async () => {
    try {
      await mutateAsync({
        tugasTambahan:
          values.length > 0 ? values : ([{}] as KinerjaSelection[]), // Required default value for BE validation
      })

      await queryClient.invalidateQueries({
        queryKey: queryKeys,
      })

      toast({
        message: 'Berhasil simpan Rencana Hasil Kerja',
        type: 'success',
      })

      router.push(`${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/tugas-tambahan`)
    } catch (e) {
      console.error(e)
      toast({
        message: 'Gagal simpan Rencana Hasil Kerja',
      })
    }
  }

  const isChanged = !isEqual(values, skp.reviewTugasTambahan)

  return (
    <div className="py-[50px] px-[116px]">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Icon onClick={handleClickBack} data-testid="icon-back">
            arrow_back
          </Icon>
          <Text variant="display-md">Ubah Rencana Hasil Kerja</Text>
        </div>

        <PanelSummary
          isLoading={!isSKPFetched}
          items={[
            {
              label: translateSKPPeriod(skp.period),
              value: skp.tahun,
            },
          ]}
        />
      </div>

      {!isSKPFetched ? (
        <Shimmer data-testid="loading-spinner-skp" />
      ) : (
        <div className="bg-white rounded-md mt-8 mb-[78px]">
          <TabTugasTambahan
            skp={skp}
            isSubmitting={false}
            editReviewData={true}
            onChange={(v) => setValues(v)}
            adjustmentInfoText="Diubah oleh Anda"
            showCheckButton={false}
            customStickyFooter={() => {
              return (
                <Button
                  data-testid="btn-save"
                  size="lg"
                  disabled={!isChanged}
                  onClick={() => {
                    setShowConfirmDialog(true)
                  }}
                >
                  Simpan
                </Button>
              )
            }}
          />
        </div>
      )}

      <DialogConfirmSaveEdit
        customSize={595}
        data-testid="dialog-confirm-save-edit"
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleSave}
      />
    </div>
  )
}
